// Site icons for favorites, history and app tiles, kept on this device only.
//
// Tiles never ask anyone for an icon: no favicon service, and no request to our own server
// either, since any such URL would carry the host of a site the student saved. Instead, when a
// page loads in a tab the tab strip already has its favicon; we shrink that to a 32px image,
// keep it here (IndexedDB, keyed by host) and reuse it. Until a site has been visited the tile
// shows a monogram. Nothing is stored in incognito or with history turned off (canStore).

const DB_NAME = "revault-site-icons";
const STORE = "icons";
export const ICON_PX = 32;
export const MAX_ICON_CHARS = 12000; // a 32px WebP/PNG is 1-4 KB; anything bigger is odd
export const MAX_ICONS = 400;

const DATA_RE = /^data:image\/(?:png|webp);base64,[A-Za-z0-9+/]+={0,2}$/;
export const validIcon = (d) => typeof d === "string" && d.length <= MAX_ICON_CHARS && DATA_RE.test(d);

// "https://www.Example.com/x" -> "example.com". Only web addresses get a key.
export function hostKey(url) {
	try {
		const u = new URL(url);
		if (u.protocol !== "http:" && u.protocol !== "https:") return "";
		return u.hostname.toLowerCase().replace(/^www\./, "").replace(/\.$/, "");
	} catch { return ""; }
}

// Pass the settings getter; icons follow the history settings.
export const canStore = (get) => get("incognito") !== true && get("keepHistory") !== false;

// --- monogram ---------------------------------------------------------------------------------
// Every color here carries white text at 4.5:1 or better (checked in test/site-icons.test.mjs), and
// a tile is a solid square, so it reads the same on dark and light looks.
export const MONO_COLORS = ["#b91c1c", "#c2410c", "#a16207", "#4d7c0f", "#15803d", "#0f766e", "#0e7490", "#0369a1", "#1d4ed8", "#4338ca", "#6d28d9", "#7e22ce", "#a21caf", "#be185d", "#9f1239", "#475569"];
function hash(s) { let h = 2166136261; for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 16777619); } return h >>> 0; }

// First letter or digit of the name (else the host), on a color picked from the host so the same
// site always looks the same.
export function monogram(name, host) {
	const pick = (s) => { const m = String(s || "").match(/[\p{L}\p{N}]/u); return m ? m[0].toUpperCase() : ""; };
	return { letter: pick(name) || pick(host) || "?", bg: MONO_COLORS[hash(String(host || name || "?").toLowerCase()) % MONO_COLORS.length], fg: "#fff" };
}

// --- storage ----------------------------------------------------------------------------------
// The backend is swappable so the store can be tested without a browser.
function idbBackend() {
	let dbp = null;
	const open = () => dbp || (dbp = new Promise((res, rej) => {
		let r;
		try { r = indexedDB.open(DB_NAME, 1); } catch (e) { rej(e); return; }
		r.onupgradeneeded = () => { if (!r.result.objectStoreNames.contains(STORE)) r.result.createObjectStore(STORE, { keyPath: "host" }); };
		r.onsuccess = () => { const db = r.result; db.onversionchange = () => { db.close(); dbp = null; }; res(db); };
		r.onerror = () => rej(r.error || new Error("indexeddb"));
	}).catch((e) => { dbp = null; throw e; }));
	const run = (mode, fn) => open().then((db) => new Promise((res, rej) => {
		const t = db.transaction(STORE, mode), q = fn(t.objectStore(STORE));
		t.oncomplete = () => res(q && q.result);
		t.onerror = t.onabort = () => rej(t.error || new Error("indexeddb"));
	}));
	return {
		all: () => run("readonly", (s) => s.getAll()),
		put: (rec) => run("readwrite", (s) => { s.put(rec); }),
		del: (host) => run("readwrite", (s) => { s.delete(host); }),
		clear: () => run("readwrite", (s) => { s.clear(); }),
	};
}

export function createIconStore(backend, opts) {
	const o = Object.assign({ allowed: () => true, max: MAX_ICONS, now: () => Date.now() }, opts);
	let mem = null; // host -> { host, data, ts }
	let loading = null;
	const subs = new Set();
	const notify = (host) => subs.forEach((fn) => { try { fn(host); } catch { /* best effort */ } });
	function load() {
		if (mem) return Promise.resolve(mem);
		if (!loading) loading = Promise.resolve().then(() => backend.all()).then((rows) => {
			mem = new Map();
			(rows || []).forEach((r) => { if (r && typeof r.host === "string" && validIcon(r.data)) mem.set(r.host, r); });
			return mem;
		}).catch(() => (mem = new Map())).finally(() => { loading = null; });
		return loading;
	}
	return {
		// Resolves to the stored data: URL for a host, or "".
		async lookup(host) {
			if (!host) return "";
			const m = await load();
			const r = m.get(host);
			return r ? r.data : "";
		},
		peek: (host) => (mem && host && mem.has(host) ? mem.get(host).data : ""),
		async save(host, data) {
			if (!host || !validIcon(data) || !o.allowed()) return false;
			const m = await load();
			const prev = m.get(host);
			const rec = { host, data, ts: o.now() };
			m.set(host, rec);
			try { await backend.put(rec); } catch { return false; }
			if (m.size > o.max) {
				const old = [...m.values()].sort((a, b) => a.ts - b.ts).slice(0, m.size - o.max);
				for (const r of old) { m.delete(r.host); try { await backend.del(r.host); } catch { /* best effort */ } }
			}
			if (!prev || prev.data !== data) notify(host);
			return true;
		},
		async clear() {
			mem = new Map();
			try { await backend.clear(); } catch { /* best effort */ }
			notify(null);
			if (o.channel) try { o.channel.postMessage("clear"); } catch { /* best effort */ }
		},
		// Another window (the Settings page) cleared the database: forget what is in memory.
		forget() { mem = null; notify(null); },
		subscribe(fn) { subs.add(fn); return () => subs.delete(fn); },
	};
}

let shared = null;
export function iconStore(allowed) {
	if (shared) return shared;
	const channel = typeof BroadcastChannel !== "undefined" ? new BroadcastChannel(DB_NAME) : null;
	const backend = typeof indexedDB !== "undefined" ? idbBackend() : { all: async () => [], put: async () => {}, del: async () => {}, clear: async () => {} };
	shared = createIconStore(backend, { channel, allowed: allowed || (() => true) });
	if (channel) channel.onmessage = (e) => { if (e.data === "clear") shared.forget(); };
	return shared;
}

// Draws an already-loaded <img> into a small square and returns it as a data: URL, or "" when the
// image is unusable (zero size, tainted, too large once encoded).
export function shrinkIcon(img, doc) {
	try {
		const w = img.naturalWidth || img.width, h = img.naturalHeight || img.height;
		if (!w || !h) return "";
		const c = (doc || document).createElement("canvas");
		c.width = c.height = ICON_PX;
		const ctx = c.getContext("2d");
		const s = Math.min(ICON_PX / w, ICON_PX / h), dw = Math.max(1, Math.round(w * s)), dh = Math.max(1, Math.round(h * s));
		ctx.imageSmoothingQuality = "high";
		ctx.drawImage(img, Math.floor((ICON_PX - dw) / 2), Math.floor((ICON_PX - dh) / 2), dw, dh);
		let d = c.toDataURL("image/webp", 0.85);
		if (!validIcon(d)) d = c.toDataURL("image/png");
		return validIcon(d) ? d : "";
	} catch { return ""; }
}
